import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Nav } from 'react-bootstrap';
import Swal from 'sweetalert2';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import GlassSelect from '../../../../core/common/glassSelect/GlassSelect';
import { isGatePassSuccess, getGatePassErrorMessage, MarkOutPayload, MarkInPayload } from '../../../../core/utils/gatePassUtils';

interface DirectGatePassModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess?: () => void;
}

interface DriverOption {
  empNumber: string;
  name: string;
}

interface VehicleOption {
  numberPlate: string;
  model: string;
  status: string;
}

export const DirectGatePassModal: React.FC<DirectGatePassModalProps> = ({ show, onHide, onSuccess }) => {
  const [activeTab, setActiveTab] = useState<'OUT' | 'IN'>('OUT');

  // Mode: Without Booking (Direct Entry) vs With Booking
  const [hasBookingOut, setHasBookingOut] = useState<boolean>(false);
  const [hasBookingIn, setHasBookingIn] = useState<boolean>(false);

  // Form states for Mark OUT
  const [outForm, setOutForm] = useState({
    bookingId: '',
    vehicleNumber: '',
    kmsOut: 0,
    outDriverId: '',
    comment: '',
  });

  // Form states for Mark IN
  const [inForm, setInForm] = useState({
    bookingId: '',
    vehicleNumber: '',
    kmsIn: 0,
    inDriverId: '',
    comment: '',
  });

  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [vehicles, setVehicles] = useState<VehicleOption[]>([]);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Fetch Drivers and Vehicles on modal open
  useEffect(() => {
    if (!show) return;

    setErrorMsg(null);
    const loadDependencies = async () => {
      setIsLoadingData(true);
      try {
        // Fetch Drivers
        const driverRes = await apiDefinitions.getUsersByType('DRIVER', {
          filterType: null,
          filterValue: '',
          date: null,
        });
        if (driverRes?.data?.status === 'S1000' || driverRes?.data?.status === 'SUCCESS') {
          const list = (driverRes.data.data?.users || []).map((d: any) => ({
            empNumber: String(d.empNumber),
            name: d.name || 'Unknown Driver',
          }));
          setDrivers(list);
        }

        // Fetch Vehicles
        const vehicleRes = await apiDefinitions.getVehicles(1);
        if (vehicleRes?.data?.status === 'S1000' || vehicleRes?.data?.status === 'SUCCESS') {
          const vList = (vehicleRes.data.data || []).map((v: any) => ({
            numberPlate: v.numberPlate,
            model: v.vehicleModel || 'Vehicle',
            status: v.vehicleStatus || 'AVAILABLE',
          }));
          setVehicles(vList);
        }
      } catch (e) {
        console.warn('Failed to load drivers/vehicles for gate pass modal:', e);
      } finally {
        setIsLoadingData(false);
      }
    };

    loadDependencies();
  }, [show]);

  const handleMarkOut = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    const vehicleNumber = outForm.vehicleNumber.trim();
    if (!vehicleNumber) {
      setErrorMsg('Vehicle Registration Number is required.');
      return;
    }
    if (!outForm.kmsOut || outForm.kmsOut <= 0) {
      setErrorMsg('Valid departure odometer reading (KMs OUT) is required.');
      return;
    }

    const payload: MarkOutPayload = {
      vehicleNumber: vehicleNumber.toUpperCase(),
      kmsOut: Number(outForm.kmsOut),
      outDriverId: outForm.outDriverId ? String(outForm.outDriverId) : undefined,
      comment: outForm.comment.trim() || undefined,
    };

    if (hasBookingOut && outForm.bookingId) {
      payload.vehicleBookingRequestId = Number(outForm.bookingId);
    }

    setIsSubmitting(true);
    try {
      const res = await apiDefinitions.markVehicleOut(payload);
      if (isGatePassSuccess(res)) {
        Swal.fire({
          icon: 'success',
          title: 'Vehicle Marked OUT',
          text: `Vehicle ${payload.vehicleNumber} marked OUT successfully.`,
          timer: 2500,
          showConfirmButton: false,
        });
        onHide();
        if (onSuccess) onSuccess();
      } else {
        setErrorMsg(res?.data?.statusDescription || 'Failed to mark vehicle out.');
      }
    } catch (err: any) {
      const friendly = getGatePassErrorMessage(err, 'Failed to mark vehicle out.');
      setErrorMsg(friendly);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    const vehicleNumber = inForm.vehicleNumber.trim();
    if (!vehicleNumber) {
      setErrorMsg('Vehicle Registration Number is required.');
      return;
    }
    if (!inForm.kmsIn || inForm.kmsIn <= 0) {
      setErrorMsg('Valid arrival odometer reading (KMs IN) is required.');
      return;
    }

    const payload: MarkInPayload = {
      vehicleNumber: vehicleNumber.toUpperCase(),
      kmsIn: Number(inForm.kmsIn),
      inDriverId: inForm.inDriverId ? String(inForm.inDriverId) : undefined,
      comment: inForm.comment.trim() || undefined,
    };

    if (hasBookingIn && inForm.bookingId) {
      payload.vehicleBookingRequestId = Number(inForm.bookingId);
    }

    setIsSubmitting(true);
    try {
      const res = await apiDefinitions.markVehicleIn(payload);
      if (isGatePassSuccess(res)) {
        Swal.fire({
          icon: 'success',
          title: 'Vehicle Marked IN',
          text: `Vehicle ${payload.vehicleNumber} returned and marked IN successfully.`,
          timer: 2500,
          showConfirmButton: false,
        });
        onHide();
        if (onSuccess) onSuccess();
      } else {
        setErrorMsg(res?.data?.statusDescription || 'Failed to mark vehicle in.');
      }
    } catch (err: any) {
      const friendly = getGatePassErrorMessage(err, 'Failed to mark vehicle in.');
      setErrorMsg(friendly);
    } finally {
      setIsSubmitting(false);
    }
  };

  const vehicleOptions = vehicles.map((v) => ({
    value: v.numberPlate,
    label: `${v.numberPlate} (${v.model}) - [${v.status}]`,
  }));

  const driverOptions = [
    { value: '', label: '-- None (No Driver) --' },
    ...drivers.map((d) => ({
      value: d.empNumber,
      label: `${d.name} (${d.empNumber})`,
    })),
  ];

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      backdrop="static"
      dialogClassName="gate-pass-modal-dialog"
      contentClassName="border-0 shadow-lg"
      style={{ overflow: 'visible' }}
    >
      <Modal.Header closeButton style={{ background: 'var(--rm-card-bg, #ffffff)', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        <Modal.Title className="d-flex align-items-center gap-2" style={{ fontSize: '1.15rem', fontWeight: 600 }}>
          <span
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i className="ti ti-barrier-block" style={{ fontSize: '1.1rem' }} />
          </span>
          <span>Security Gate Pass &bull; Vehicle Departure / Arrival</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: '26px 30px', minHeight: '520px', overflow: 'visible' }}>
        {/* Navigation Tabs */}
        <Nav variant="pills" className="nav-fill mb-4 p-1 rounded-pill" style={{ background: 'rgba(0,0,0,0.04)' }}>
          <Nav.Item>
            <Nav.Link
              active={activeTab === 'OUT'}
              onClick={() => { setActiveTab('OUT'); setErrorMsg(null); }}
              className="d-flex align-items-center justify-content-center gap-2 rounded-pill fw-semibold py-2"
              style={{
                transition: 'all 0.25s ease',
                background: activeTab === 'OUT' ? 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' : 'transparent',
                color: activeTab === 'OUT' ? '#ffffff' : '#64748b',
              }}
            >
              <i className="ti ti-arrow-up-right" />
              <span>Mark Vehicle OUT (Departure)</span>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              active={activeTab === 'IN'}
              onClick={() => { setActiveTab('IN'); setErrorMsg(null); }}
              className="d-flex align-items-center justify-content-center gap-2 rounded-pill fw-semibold py-2"
              style={{
                transition: 'all 0.25s ease',
                background: activeTab === 'IN' ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)' : 'transparent',
                color: activeTab === 'IN' ? '#ffffff' : '#64748b',
              }}
            >
              <i className="ti ti-arrow-down-left" />
              <span>Mark Vehicle IN (Arrival)</span>
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {errorMsg && (
          <Alert variant="danger" className="d-flex align-items-center gap-2 py-2 px-3 mb-3">
            <i className="ti ti-alert-triangle flex-shrink-0" />
            <span style={{ fontSize: '0.88rem' }}>{errorMsg}</span>
          </Alert>
        )}

        {/* -------------------- TAB 1: MARK OUT -------------------- */}
        {activeTab === 'OUT' && (
          <Form onSubmit={handleMarkOut}>
            <div className="card p-3 mb-3 border-0" style={{ background: 'rgba(239, 68, 68, 0.04)', borderRadius: '12px' }}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-semibold text-danger small">
                  <i className="ti ti-info-circle me-1" />
                  Recording departure mileage & changes status to ONGOING_TRIP
                </span>
                <Form.Check
                  type="switch"
                  id="hasBookingOutSwitch"
                  label={<span className="small fw-semibold">Link to Booking Request</span>}
                  checked={hasBookingOut}
                  onChange={(e) => setHasBookingOut(e.target.checked)}
                />
              </div>

              {hasBookingOut && (
                <div className="mb-2">
                  <Form.Label className="small fw-bold">Vehicle Booking Request ID *</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g. 142"
                    value={outForm.bookingId}
                    onChange={(e) => setOutForm((prev) => ({ ...prev, bookingId: e.target.value }))}
                    required={hasBookingOut}
                  />
                  <Form.Text className="text-muted small">Enter the booking request ID to attach to this trip.</Form.Text>
                </div>
              )}
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Vehicle Number / Plate *</Form.Label>
                  {vehicles.length > 0 ? (
                    <GlassSelect
                      options={vehicleOptions}
                      value={outForm.vehicleNumber}
                      onChange={(val) => setOutForm((prev) => ({ ...prev, vehicleNumber: val }))}
                      placeholder="Select Vehicle Number"
                      searchable
                      allowClear
                    />
                  ) : (
                    <Form.Control
                      type="text"
                      placeholder="e.g. WP-CAB-1234"
                      value={outForm.vehicleNumber}
                      onChange={(e) => setOutForm((prev) => ({ ...prev, vehicleNumber: e.target.value.toUpperCase() }))}
                      required
                    />
                  )}
                  <Form.Text className="text-muted small">Or type manually if not listed above</Form.Text>
                  {vehicles.length > 0 && (
                    <div className="mt-1">
                      <Form.Control
                        size="sm"
                        type="text"
                        placeholder="Manual plate entry (e.g. WP-CAB-1234)"
                        value={outForm.vehicleNumber}
                        onChange={(e) => setOutForm((prev) => ({ ...prev, vehicleNumber: e.target.value.toUpperCase() }))}
                      />
                    </div>
                  )}
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Departure Mileage (KMs OUT) *</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="number"
                      placeholder="e.g. 15420"
                      value={outForm.kmsOut || ''}
                      onChange={(e) => setOutForm((prev) => ({ ...prev, kmsOut: Number(e.target.value) }))}
                      min="1"
                      required
                    />
                    <span className="input-group-text">KM</span>
                  </div>
                  <Form.Text className="text-muted small">Must be &ge; current vehicle odometer</Form.Text>
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Driver (Optional)</Form.Label>
                  {drivers.length > 0 ? (
                    <GlassSelect
                      options={driverOptions}
                      value={outForm.outDriverId}
                      onChange={(val) => setOutForm((prev) => ({ ...prev, outDriverId: val }))}
                      placeholder="Select Driver"
                      searchable
                      allowClear
                      placement="auto"
                    />
                  ) : (
                    <Form.Control
                      type="text"
                      placeholder="Driver User ID / Employee #"
                      value={outForm.outDriverId}
                      onChange={(e) => setOutForm((prev) => ({ ...prev, outDriverId: e.target.value }))}
                    />
                  )}
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Trip Comment / Reason</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Urgent delivery to branch / Maintenance"
                    value={outForm.comment}
                    onChange={(e) => setOutForm((prev) => ({ ...prev, comment: e.target.value }))}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
              <Button variant="light" onClick={onHide} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                variant="danger"
                type="submit"
                disabled={isSubmitting}
                className="d-flex align-items-center gap-2 px-4"
              >
                <i className="ti ti-arrow-up-right" />
                <span>{isSubmitting ? 'Marking Out...' : 'Confirm Mark OUT'}</span>
              </Button>
            </div>
          </Form>
        )}

        {/* -------------------- TAB 2: MARK IN -------------------- */}
        {activeTab === 'IN' && (
          <Form onSubmit={handleMarkIn}>
            <div className="card p-3 mb-3 border-0" style={{ background: 'rgba(16, 185, 129, 0.04)', borderRadius: '12px' }}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-semibold text-success small">
                  <i className="ti ti-info-circle me-1" />
                  Captures return mileage & resets status back to AVAILABLE
                </span>
                <Form.Check
                  type="switch"
                  id="hasBookingInSwitch"
                  label={<span className="small fw-semibold">Link to Booking Request</span>}
                  checked={hasBookingIn}
                  onChange={(e) => setHasBookingIn(e.target.checked)}
                />
              </div>

              {hasBookingIn && (
                <div className="mb-2">
                  <Form.Label className="small fw-bold">Vehicle Booking Request ID *</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g. 142"
                    value={inForm.bookingId}
                    onChange={(e) => setInForm((prev) => ({ ...prev, bookingId: e.target.value }))}
                    required={hasBookingIn}
                  />
                  <Form.Text className="text-muted small">Enter the booking request ID to attach to this return.</Form.Text>
                </div>
              )}
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Vehicle Number / Plate *</Form.Label>
                  {vehicles.length > 0 ? (
                    <GlassSelect
                      options={vehicleOptions}
                      value={inForm.vehicleNumber}
                      onChange={(val) => setInForm((prev) => ({ ...prev, vehicleNumber: val }))}
                      placeholder="Select Vehicle Number"
                      searchable
                      allowClear
                    />
                  ) : (
                    <Form.Control
                      type="text"
                      placeholder="e.g. WP-CAB-1234"
                      value={inForm.vehicleNumber}
                      onChange={(e) => setInForm((prev) => ({ ...prev, vehicleNumber: e.target.value.toUpperCase() }))}
                      required
                    />
                  )}
                  <Form.Text className="text-muted small">Or type manually if not listed above</Form.Text>
                  {vehicles.length > 0 && (
                    <div className="mt-1">
                      <Form.Control
                        size="sm"
                        type="text"
                        placeholder="Manual plate entry (e.g. WP-CAB-1234)"
                        value={inForm.vehicleNumber}
                        onChange={(e) => setInForm((prev) => ({ ...prev, vehicleNumber: e.target.value.toUpperCase() }))}
                      />
                    </div>
                  )}
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Arrival Mileage (KMs IN) *</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="number"
                      placeholder="e.g. 15510"
                      value={inForm.kmsIn || ''}
                      onChange={(e) => setInForm((prev) => ({ ...prev, kmsIn: Number(e.target.value) }))}
                      min="1"
                      required
                    />
                    <span className="input-group-text">KM</span>
                  </div>
                  <Form.Text className="text-muted small">Must be &ge; current vehicle departure mileage</Form.Text>
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Driver (Optional)</Form.Label>
                  {drivers.length > 0 ? (
                    <GlassSelect
                      options={driverOptions}
                      value={inForm.inDriverId}
                      onChange={(val) => setInForm((prev) => ({ ...prev, inDriverId: val }))}
                      placeholder="Select Driver"
                      searchable
                      allowClear
                      placement="auto"
                    />
                  ) : (
                    <Form.Control
                      type="text"
                      placeholder="Driver User ID / Employee #"
                      value={inForm.inDriverId}
                      onChange={(e) => setInForm((prev) => ({ ...prev, inDriverId: e.target.value }))}
                    />
                  )}
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Return Remarks / Inspection</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Returned safely without any issues"
                    value={inForm.comment}
                    onChange={(e) => setInForm((prev) => ({ ...prev, comment: e.target.value }))}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
              <Button variant="light" onClick={onHide} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                variant="success"
                type="submit"
                disabled={isSubmitting}
                className="d-flex align-items-center gap-2 px-4"
              >
                <i className="ti ti-arrow-down-left" />
                <span>{isSubmitting ? 'Marking In...' : 'Confirm Mark IN'}</span>
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default DirectGatePassModal;
